// src/components/quotes/QuoteTemplateSaveModal.jsx
//
// "Save as template" — the three-way choice that a window.confirm could not
// express. Until this existed the mode was inferred from where the cursor
// happened to be (OK = the focused section, Cancel = the focused option's
// items), a guess the save code itself flagged as one that "would silently
// save the wrong items", and there was NO path that saved the whole quote at
// all — every template was one section at most, however many the quote had.
//
//   • Whole quote — every section in order, every option and item, plus the
//     common items. Freight is excluded (derived; recalculates on the target).
//   • This section — the focused section with its 2+ options. Kept: "Turret
//     Cameras: G4 Pro | G5 Pro" is a reusable unit on its own.
//   • These items — the focused option's (or Common's) items as a flat bag.
//
// The words and counts are computed by MspOsApp (it owns focus and the
// helpers); this component only asks the question. Nothing is written until
// Save.
import React, { useEffect, useState } from "react";

export default function QuoteTemplateSaveModal({ choices, onSave, onCancel }) {
  const { quoteTitle = "Template", whole, section, items } = choices || {};
  const modes = [
    whole && { id: "whole", label: "Whole quote", detail: `${whole.sections} section${whole.sections === 1 ? "" : "s"} · ${whole.items} item${whole.items === 1 ? "" : "s"}${whole.commons ? ` · ${whole.commons} common` : ""}${whole.freightExcluded ? " · freight left out (it recalculates)" : ""}`, name: quoteTitle },
    section && { id: "section", label: `This section — "${section.label}"`, detail: `${section.options} options · ${section.items} item${section.items === 1 ? "" : "s"}`, name: `${quoteTitle} — ${section.label}` },
    items && { id: "items", label: `These items — ${items.label}`, detail: `${items.count} item${items.count === 1 ? "" : "s"}, as a flat list`, name: `${quoteTitle} — ${items.label}` },
  ].filter(Boolean);

  const [mode, setMode] = useState(modes[0]?.id || "whole");
  const [name, setName] = useState(modes[0]?.name || quoteTitle);
  const [touched, setTouched] = useState(false);

  // The default name follows the mode until Craig types his own.
  useEffect(() => {
    if (!touched) setName(modes.find((m) => m.id === mode)?.name || quoteTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const canSave = modes.length > 0 && name.trim().length > 0;

  return (
    <div data-testid="quote-template-save-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onCancel}>
      <div style={{ background: "var(--s1)", borderRadius: 10, padding: 22, width: 520, maxWidth: "100%", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, color: "var(--text)", marginBottom: 4 }}>Save as template</div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 12, lineHeight: 1.6 }}>
          What should the template hold? Pricing and cost come across; terms, client details and freight do not.
        </div>
        <div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          {modes.map((m) => (
            <label key={m.id} data-testid={`mode-${m.id}`}
              style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${mode === m.id ? "var(--strat)" : "var(--border)"}`, background: mode === m.id ? "var(--strat-d)" : "white" }}>
              <input type="radio" name="tpl-mode" value={m.id} checked={mode === m.id} onChange={() => setMode(m.id)} style={{ marginTop: 2 }} />
              <span>
                <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{m.label}</span>
                <span style={{ display: "block", fontSize: 10, color: "var(--text3)" }}>{m.detail}</span>
              </span>
            </label>
          ))}
          {modes.length === 0 && <div style={{ fontSize: 11, color: "var(--danger)" }}>Nothing to save — this quote has no line items yet.</div>}
        </div>
        <label style={{ fontSize: 10, color: "var(--text3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Template name</label>
        <input className="inp" data-testid="template-name" value={name} style={{ width: "100%", fontSize: 12, marginTop: 4, marginBottom: 14 }}
          onChange={(e) => { setTouched(true); setName(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter" && canSave) onSave({ mode, name: name.trim() }); }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" style={{ fontSize: 11 }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-strat" data-testid="template-save" style={{ fontSize: 11 }} disabled={!canSave} onClick={() => onSave({ mode, name: name.trim() })}>Save template</button>
        </div>
      </div>
    </div>
  );
}
